'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { Button } from '@repo/design-system-cf/components/button'
import { Typography } from '@repo/design-system-cf/components/typography'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { IconDownload } from '@repo/design-system-cf/components/icons'
import {
  DownloadStatus,
  ImageStatus,
} from '@repo/rpc-types-creativefabrica-common-v1/imageprocessor/v1'
import { downloadMediaFile } from 'utils/download-media-file'
import {
  useDownloadMutation,
  useGetDownloadQuery,
  useGetImagesInfiniteQuery,
} from '@web/gen/service/creativefabrica/backgroundremover/v2'
import { getSessionImagesVariables } from './session-images-query'

const SESSION_IMAGES_STALE_TIME_MS = 5 * 60 * 1000
const PROCESSING_STATUSES = new Set<ImageStatus>([ImageStatus.REQUESTED, ImageStatus.UPLOADED])

type ResultView = 'comparison' | 'original' | 'result'

export function BackgroundRemoverResult({ sessionId }: { sessionId: string }) {
  return <ResultBody sessionId={sessionId} />
}

function ResultBody({ sessionId }: { sessionId: string }) {
  const t = useTranslations('pages.background-remover')
  const [view, setView] = React.useState<ResultView>('comparison')

  const imagesQuery = useGetImagesInfiniteQuery(getSessionImagesVariables(sessionId), {
    staleTime: SESSION_IMAGES_STALE_TIME_MS,
    refetchInterval: query => {
      const image = query.state.data?.pages[0]?.images?.[0]
      if (!image?.status) {
        return false
      }
      if (PROCESSING_STATUSES.has(image.status)) {
        return 1000
      }
      return false
    },
  })

  const image = imagesQuery.data?.pages[0]?.images?.[0]
  const download = useSessionDownload(image?.id, image?.fileName)
  const isFailed = image?.status === ImageStatus.ERROR || imagesQuery.isError
  const isReady = image?.status === ImageStatus.COMPLETED
  const isLoadingSession = imagesQuery.isPending && !image
  const isProcessing = Boolean(image) && !isReady && !isFailed
  let statusMessage: string | null = null
  if (isLoadingSession) {
    statusMessage = t('loading')
  } else if (isProcessing) {
    statusMessage = t('processing')
  }
  const resultUrl = image?.previewUrl || image?.responseUrl
  const originalUrl = image?.originalPreviewUrl || image?.originalUrl

  return (
    <div className="flex h-full min-h-[480px] flex-col gap-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Button
            size="small"
            variant={view === 'comparison' ? 'primary' : 'secondary'}
            onClick={() => setView('comparison')}
          >
            {t('compare')}
          </Button>
          <Button
            size="small"
            variant={view === 'original' ? 'primary' : 'secondary'}
            onClick={() => setView('original')}
          >
            {t('original')}
          </Button>
          <Button
            size="small"
            variant={view === 'result' ? 'primary' : 'secondary'}
            onClick={() => setView('result')}
          >
            {t('result')}
          </Button>
        </div>
        <Button
          variant="primary"
          iconLeft={<IconDownload className="h-5 w-5" />}
          disabled={!isReady}
          loading={download.isPending}
          loadingText={t('downloading')}
          onClick={() => {
            void download.download()
          }}
        >
          {t('download')}
        </Button>
      </div>

      {download.isError ? (
        <Typography variant="body-01" className="text-cf-red-800">
          {t('download_failed')}
        </Typography>
      ) : null}

      {isFailed ? (
        <Typography variant="body-01" className="text-cf-red-800">
          {t('failed')}
        </Typography>
      ) : null}

      {statusMessage ? (
        <div className="flex items-center gap-3">
          <Spinner />
          <Typography variant="body-01">{statusMessage}</Typography>
        </div>
      ) : null}

      {isReady && resultUrl ? (
        <ResultCanvas view={view} originalUrl={originalUrl} resultUrl={resultUrl} />
      ) : null}
    </div>
  )
}

function ResultCanvas({
  view,
  originalUrl,
  resultUrl,
}: {
  view: ResultView
  originalUrl?: string
  resultUrl: string
}) {
  const t = useTranslations('pages.background-remover')

  if (view === 'comparison') {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {originalUrl ? <ImagePanel label={t('original')} src={originalUrl} /> : null}
        <ImagePanel label={t('result')} src={resultUrl} />
      </div>
    )
  }

  if (view === 'original' && originalUrl) {
    return <ImagePanel label={t('original')} src={originalUrl} />
  }

  return <ImagePanel label={t('result')} src={resultUrl} />
}

function ImagePanel({ label, src }: { label: string; src: string }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <Typography variant="cf-heading-compact-01">{label}</Typography>
      <div className="flex min-h-[320px] flex-1 items-center justify-center overflow-hidden rounded-2 border border-cf-grey-200 bg-cf-grey-50">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt="" className="max-h-[min(560px,60vh)] max-w-full object-contain" />
      </div>
    </div>
  )
}

function useSessionDownload(imageId: string | undefined, fileName: string | undefined) {
  const [downloadId, setDownloadId] = React.useState<string | null>(null)
  const [hasDownloadFailed, setHasDownloadFailed] = React.useState(false)
  const downloadMutation = useDownloadMutation()
  const downloadQuery = useGetDownloadQuery(
    { downloadId: downloadId ?? '' },
    {
      enabled: Boolean(downloadId),
      refetchInterval: query => {
        const status = query.state.data?.download?.status
        if (status === DownloadStatus.COMPLETED || status === DownloadStatus.ERROR) {
          return false
        }
        return 1000
      },
    },
  )

  const downloadUrl = downloadQuery.data?.download?.url
  const downloadStatus = downloadQuery.data?.download?.status

  React.useEffect(() => {
    if (!downloadUrl) {
      return
    }
    void downloadMediaFile({
      url: downloadUrl,
      fileName: fileName || 'background-removed.png',
      mimeType: 'image/png',
    })
    setDownloadId(null)
  }, [downloadUrl, fileName])

  React.useEffect(() => {
    if (downloadStatus !== DownloadStatus.ERROR && !downloadQuery.isError) {
      return
    }
    setHasDownloadFailed(true)
    setDownloadId(null)
  }, [downloadStatus, downloadQuery.isError])

  return {
    isPending: downloadMutation.isPending || Boolean(downloadId),
    isError: hasDownloadFailed,
    download: async () => {
      if (!imageId) {
        return
      }
      setHasDownloadFailed(false)
      try {
        const created = await downloadMutation.mutateAsync({
          download: {
            case: 'imageIds',
            value: {
              imageIds: [imageId],
            },
          },
        })
        if (created.downloadId) {
          setDownloadId(created.downloadId)
        }
      } catch {
        setHasDownloadFailed(true)
      }
    },
  }
}
